import { toast } from 'react-hot-toast'

import { FvButton, FvLinkButton, FvNum, Icon } from '@fv/client-components'
import { fvDate } from '@fv/dates'

import { DividedContent } from '@/components/DividedContent'
import { Pinline } from '@/components/Pinline'
import { ShipperTooltip } from '@/components/ShipperTooltip'
import { Price } from '@/components/shared/Price'

import { useDismissSalt, useRequestSalt, useSalt } from './api'

type SaltBannerProps = {
  loadId?: string
}

export const SaltBanner = ({ loadId }: SaltBannerProps) => {
  const { data: salt, isLoading } = useSalt(loadId)
  const { mutate: requestSalt, isPending } = useRequestSalt(loadId)
  const { mutate: dismissSalt, isPending: isDismissing } =
    useDismissSalt(loadId)

  if (isLoading || !salt) return null

  const formattedDate = salt.bookedDate
    ? fvDate(salt.bookedDate).format('M/D/YY')
    : null
  const isRequestSent = salt.isRequested

  return (
    <div className="bg-fv-green-50 border-b border-fv-green-300 px-6 py-3 flex items-center fa-width-auto animate-loadMatchFlash">
      <Icon
        icon="check-circle-sharp-duotone"
        className="opacity-40 text-fv-green-500 text-[1.5rem] [--fa-primary-color:currentColor] [--fa-secondary-color:#fff] [--fa-secondary-opacity:1] animate-[spin_0.3s_ease-in-out_1_300ms,fadeIn_0.35s_ease-in-out_300ms_forwards]"
      />

      <div className="flex basis-auto items-center gap-x-1 whitespace-nowrap overflow-hidden ml-2">
        {isRequestSent ? (
          <span>
            You sent a request to {salt.carrierName} for{' '}
            <Price
              amount={salt.amount}
              currency={salt.currency}
              size="sm"
              className="bg-fv-green-100 border-fv-green-300 border p-1 rounded-xs ml-1"
            />
          </span>
        ) : (
          <>
            <div className="fa-width-auto overflow-hidden flex items-center">
              <span className="truncate">
                You used{' '}
                <span className="underline underline-offset-4 decoration-fv-green-300">
                  {salt.carrierName}
                </span>
              </span>{' '}
            </div>
            {formattedDate && (
              <div className="">
                for these lanes on{' '}
                <FvLinkButton
                  to={`/details/${salt.loadId}`}
                  theme="underlined"
                  target="_blank"
                  className="font-semibold"
                >
                  {formattedDate}
                </FvLinkButton>
              </div>
            )}
            <Price
              amount={salt.amount}
              currency={salt.currency}
              size="sm"
              className="bg-fv-green-100 border-fv-green-300 border p-1 rounded-xs ml-1"
            />
          </>
        )}
      </div>
      {!isRequestSent && (
        <div className="flex items-center flex-1">
          <Pinline className="!border-fv-green flex-1 mx-2 min-w-8" dashed />
          <DividedContent>
            <ShipperTooltip
              label={
                <>
                  We will send a request to {salt.carrierName} for{' '}
                  <FvNum val={salt.amount} currency={salt.currency} />.
                </>
              }
            >
              <FvButton
                icon="check"
                theme="underlined"
                loading={isPending}
                onClick={() => {
                  if (
                    !window.confirm(
                      `This will send a request to ${salt.carrierName} suggesting the same rate as the last time. Proceed?`,
                    )
                  ) {
                    return
                  }

                  requestSalt(null, {
                    onSuccess: () => {
                      toast.success(`Request sent to ${salt.carrierName}`, {
                        duration: 5000,
                      })
                    },
                  })
                }}
              >
                Request same as last time
              </FvButton>
            </ShipperTooltip>
            <ShipperTooltip label="No thanks, I prefer something else">
              <FvButton
                icon="times"
                iconClass="text-fv-orange-bright"
                disabled={isDismissing}
                onClick={() => {
                  dismissSalt(null)
                }}
              />
            </ShipperTooltip>
          </DividedContent>
        </div>
      )}
    </div>
  )
}
